import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Footer } from "@/components/Footer";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { Skeleton } from "@/components/ui/skeleton";

// Images
import heroImage from "@/assets/fireplace-installation-mahwah-nj-hero.webp";
import fireplaceWayneDuring from "@/assets/fireplace-install-wayne-passaic-county-nj-during.webp";
import fireplaceWayneProgress from "@/assets/fireplace-installation-wayne-passaic-county-nj.webp";
import fireplaceWayneScaffold from "@/assets/fireplace-installation-wayne-nj-m.webp";

// Citation link component - wraps text with citation number
const CitationLink = ({ num, url, children }: { num: number; url: string; children?: React.ReactNode }) => (
  <a 
    href={url} 
    target="_blank" 
    rel="noopener noreferrer" 
    className="text-secondary hover:underline"
  >
    {children}<sup>{num}</sup>
  </a>
);

// Placeholder image component for future images
const ImagePlaceholder = ({ alt, className = "" }: { alt: string; className?: string }) => (
  <div className={`bg-muted rounded-xl flex items-center justify-center ${className}`}>
    <div className="text-center p-8">
      <Skeleton className="w-full h-full absolute inset-0 rounded-xl" />
      <span className="relative text-muted-foreground text-sm">{alt}</span>
    </div>
  </div>
);

const faqs = [
  {
    question: "Do I need a permit to install a fireplace in North Jersey?",
    answer: "Yes. The New Jersey Uniform Construction Code requires a construction permit for all new fireplace installations. Your local building department handles the application and schedules the inspection. Work can't start until the permit is approved.",
  },
  {
    question: "Can a masonry contractor add a fireplace to a home that doesn't already have one?",
    answer: "Yes. We can build a new fireplace and chimney from the ground up or install a factory-built unit with a new chase. The right option depends on your home's structure and what your foundation can support.",
  },
  {
    question: "What type of fireplace works best for North Jersey winters?",
    answer: "Wood-burning masonry fireplaces put out the most heat and last the longest. Gas direct-vent units are a good option too, especially if you don't have a chimney. Both hold up well in the Northeast climate.",
  },
  {
    question: "How long does fireplace installation take in North Jersey?",
    answer: "A gas insert may take 1 to 2 days. A full masonry fireplace with chimney can take 2 to 4 weeks depending on permit timelines and weather. If you want it ready for winter, plan to schedule before fall.",
  },
  {
    question: "Does adding a fireplace increase home value in North Jersey?",
    answer: "In cold-climate markets like North Jersey, fireplaces are still a desirable feature. The NAHB shows gas fireplaces rank high on buyer preference lists. NAR data suggests fireplaces can add 1 to 3 percent to home value.",
  },
  {
    question: "Do I need a professional to install a fireplace?",
    answer: "Yes. New Jersey requires a licensed contractor for any project that involves a building permit. NFPA 211 also calls for professional installation on all fireplace types. Make sure your contractor holds a valid NJ Home Improvement Contractor registration.",
  },
];

const citations = [
  {
    num: 1,
    text: "NFPA 211: Standard for Chimneys, Fireplaces, Vents, and Solid Fuel-Burning Appliances (National Fire Protection Association)",
    url: "https://www.nfpa.org/codes-and-standards/nfpa-211-standard-development/211",
  },
  {
    num: 2,
    text: "EPA Ordinances and Regulations for Wood-Burning Appliances (U.S. Environmental Protection Agency)",
    url: "https://www.epa.gov/burnwise/ordinances-and-regulations-wood-burning-appliances",
  },
  {
    num: 3,
    text: "What Home Buyers Really Want, 2024 Edition (National Association of Home Builders)",
    url: "https://www.nahb.org/blog/2024/04/what-home-buyers-really-want-2024",
  },
  {
    num: 4,
    text: "New Jersey Uniform Construction Code, NJAC 5:23 (NJ Dept. of Community Affairs)",
    url: "https://www.nj.gov/dca/codes/codreg/ucc.shtml",
  },
];

const youtubeShorts = [
  {
    url: "https://www.youtube.com/embed/KFgLmBVqRZQ",
    title: "Fireplace Installation Video 1",
  },
];

export default function FireplaceInstallation() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Fireplace Installation Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="Need a new fireplace in North Jersey? We build wood-burning and gas fireplaces from the ground up. Fully permitted and built for tough Northeast winters." />
        <meta property="og:description" content="Need a new fireplace in North Jersey? We build wood-burning and gas fireplaces from the ground up. Fully permitted and built for tough Northeast winters." />
        <meta property="og:title" content="Fireplace Installation Totowa, NJ | Masonry Pro Construction" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Fireplace Installation",
            description: "Wood-burning and gas fireplace installation in North Jersey. Fully permitted, code-compliant, built for Northeast winters.",
            url: "/masonry-services/fireplace-installation",
            image: "https://masonry-pro.com/og/fireplace-installation.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/fireplace-installation.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/fireplace-installation.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Fireplace installation in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(13,16,23,0.40), rgba(13,16,23,0.65))' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Fireplace Installation in Totowa, NJ Serving <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>North Jersey</span>
                </h1>
                <Button variant="gold" size="lg" asChild>
                  <a href="tel:+19737252395">Schedule Your Free Assessment</a>
                </Button>
              </div>
            </div>
          </section>

          {/* Intro Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto prose prose-lg">
                <p className="text-lg text-muted-foreground">
                  Thinking about adding a fireplace to your home? Fireplace installation in North Jersey is one of the best ways to add warmth and boost your resale value. We build wood-burning, gas, and full masonry fireplaces for homes across <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link>, <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic</Link>, <Link to="/service-areas/essex-county" className="text-secondary hover:underline">Essex</Link>, and <Link to="/service-areas/morris-county" className="text-secondary hover:underline">Morris</Link> counties. Every project needs a permit, and timelines vary by fireplace type. Our licensed masonry contractors build fireplaces that hold up through the tough Northeast winters. Every outdoor fireplace is built by the same father-and-son crew at <Link to="/" className="text-secondary hover:underline">Masonry Pro Construction</Link>.
                </p>
              </div>
            </div>
          </section>

          {/* Types of Fireplaces Section - Content LEFT, Placeholder RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Most North Jersey Homes <span className="text-gradient-gold">Can Support a New Fireplace</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      You'd be surprised how many homes can install a fireplace, even ones that don't have a chimney right now. There are three main types we install:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Masonry-built</strong> is the traditional route. You need a concrete foundation for it, and there has to be enough clearance between the firebox and any combustible material. That's per code, <CitationLink num={1} url="https://www.nfpa.org/codes-and-standards/nfpa-211-standard-development/211">NFPA 211</CitationLink>.</li>
                      <li><strong>Factory-built (prefab) units</strong> are lighter. They can work in homes where going full masonry isn't an option.</li>
                      <li><strong>Gas direct-vent</strong> is what we recommend when there's no chimney at all. These fireplaces vent through an exterior wall using sealed combustion and no flue is needed.</li>
                    </ul>
                    <p>
                      A lot of older homes across Bergen, Passaic, and Essex counties were built before 1960 and many of them still have chimney infrastructure that can be used or rebuilt. Newer homes out in Wyckoff, Montclair, or Wayne may need a chimney built from scratch.
                    </p>
                    <p>
                      Can you do this yourself? Not in New Jersey. Any fireplace project that needs a permit has to go through a licensed contractor and NFPA 211 backs that up. It's a safety thing, not just a code thing.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden relative">
                  <img 
                    src={fireplaceWayneDuring} 
                    alt="Mason installing a stone veneer on an outdoor fireplace in Wayne, Passaic County, New Jersey, 07470" 
                    className="w-full h-full object-cover rotate-90 scale-125"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Choose the Right Type Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Choose the Right Fireplace Type <span className="text-gradient-gold">for Your North Jersey Home</span>
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Each fireplace type gives you something different. Heat output, maintenance, cost, and resale impact all vary. Here's how they break down:
                </p>
                
                {/* Fireplace Comparison Table */}
                <div className="overflow-x-auto mb-8">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-muted">
                        <th className="border border-border p-3 text-left font-semibold">Type</th>
                        <th className="border border-border p-3 text-left font-semibold">Efficiency</th>
                        <th className="border border-border p-3 text-left font-semibold">Chimney Required</th>
                        <th className="border border-border p-3 text-left font-semibold">Maintenance</th>
                        <th className="border border-border p-3 text-left font-semibold">Best For</th>
                      </tr>
                    </thead>
                    <tbody className="text-muted-foreground">
                      <tr>
                        <td className="border border-border p-3">Wood-burning masonry</td>
                        <td className="border border-border p-3">High</td>
                        <td className="border border-border p-3">Yes</td>
                        <td className="border border-border p-3">Annual cleaning required</td>
                        <td className="border border-border p-3">Homeowners who want a traditional fireplace built to last</td>
                      </tr>
                      <tr className="bg-muted/50">
                        <td className="border border-border p-3">Gas direct-vent</td>
                        <td className="border border-border p-3">Moderate</td>
                        <td className="border border-border p-3">No</td>
                        <td className="border border-border p-3">Low, occasional servicing</td>
                        <td className="border border-border p-3">Newer homes or homes without a chimney</td>
                      </tr>
                      <tr>
                        <td className="border border-border p-3">Electric</td>
                        <td className="border border-border p-3">Low</td>
                        <td className="border border-border p-3">No</td>
                        <td className="border border-border p-3">Minimal</td>
                        <td className="border border-border p-3">Budget installs, supplemental heat only</td>
                      </tr>
                      <tr className="bg-muted/50">
                        <td className="border border-border p-3">Masonry-built (custom)</td>
                        <td className="border border-border p-3">High</td>
                        <td className="border border-border p-3">Yes</td>
                        <td className="border border-border p-3">Periodic inspection</td>
                        <td className="border border-border p-3">Custom stone or brick finish, long-term durability</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    Wood-burning fireplaces have that classic feel. But they do need a chimney and yearly cleaning to stay safe. If your home in Morris County or the surrounding area lacks chimney access, a direct-vent gas unit may be the better option. Sealed combustion, no flue, and they're the most requested fireplace type among buyers in the Northeast, according to the <CitationLink num={3} url="https://www.nahb.org/blog/2024/04/what-home-buyers-really-want-2024">NAHB</CitationLink>.
                  </p>
                  <p>
                    Electric units cost the least to install. Fair enough. But they put out limited heat and won't carry the same weight at resale.
                  </p>
                  <p>
                    And if you want something that really holds up, a masonry-built fireplace with custom stone or brick is hard to beat. We build these across Ridgewood, Glen Rock, and Chatham. Fireplaces can add anywhere from 1 to 3 percent to your home's value per NAR estimates, and that number tends to hold stronger in cold-climate markets like North Jersey.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Code Requirements Section - Placeholder LEFT, Content RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="aspect-[4/3] rounded-xl overflow-hidden relative">
                  <img 
                    src={fireplaceWayneProgress} 
                    alt="Stone fireplace installation in progress on a new home in Wayne, Passaic County, New Jersey, 07470" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    What North Jersey Homeowners <span className="text-gradient-gold">Need Before Installation Begins</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Before any work starts, you'll need a construction permit. New Jersey enforces the <CitationLink num={4} url="https://www.nj.gov/dca/codes/codreg/ucc.shtml">Uniform Construction Code (NJAC 5:23)</CitationLink>, and every new fireplace install falls under it. Your local building department in Paramus, Hackensack, Clifton, or wherever you are in North Jersey handles the application and inspection.
                    </p>
                    <p>Here's what the code covers for fireplaces specifically:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Firebox depth: 20 inches minimum</li>
                      <li>Hearth extension: 16 inches out from the front, 8 inches on each side</li>
                      <li>Combustible clearance: At least 6 inches from the fireplace opening per NFPA 211, Section 11.2.5.3</li>
                      <li>Chimney verification: The UCC-F370 form is required for any new fireplace</li>
                    </ul>
                    <p>
                      One more thing. Make sure whoever you hire holds a valid NJ Home Improvement Contractor registration. That's separate from a general business license. If they can't show you that, keep looking.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Installation Steps Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  How a Masonry Contractor <span className="text-gradient-gold">Installs a Fireplace Step by Step</span>
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Knowing what goes into a fireplace install helps you plan your timeline and avoid surprises. Here's how we handle it from start to finish:
                </p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    { step: 1, title: "Site Inspection", desc: "We inspect foundation, framing, and chimney path. Not every wall or room works." },
                    { step: 2, title: "Pull the Permit", desc: "We handle the construction permit through your local NJ building department." },
                    { step: 3, title: "Foundation and Hearth Prep", desc: "Footings go below frost line. Hearth pad built to code." },
                    { step: 4, title: "Firebox and Smoke Chamber", desc: "Construct masonry firebox, smoke chamber, install flue liner." },
                    { step: 5, title: "Damper and Chimney Cap", desc: "Ferrous metal damper per NJ code. Chimney cap and flashing." },
                    { step: 6, title: "Finish Work", desc: "Stone veneer, brick, mantel, hearth extension applied." },
                    { step: 7, title: "Final Inspection", desc: "Municipality sends inspector. Certificate of Approval issued." },
                  ].map((item) => (
                    <div key={item.step} className="bg-background rounded-xl p-6 border border-border">
                      <div className="w-10 h-10 rounded-full bg-gradient-navy flex items-center justify-center mb-4">
                        <span className="text-secondary font-bold">{item.step}</span>
                      </div>
                      <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-8 p-6 bg-muted rounded-xl">
                  <p className="text-lg text-muted-foreground">
                    <strong>Common mistakes we see:</strong> Skipping the chimney liner, undersizing the flue, and not maintaining proper clearance to combustibles. Those are the three that cause the most problems down the road. Per NFPA 211, masonry over any opening has to be supported by noncombustible lintels. That's a detail some contractors overlook.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Maintenance Section - Content LEFT, Placeholder RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Keep Your New Fireplace <span className="text-gradient-gold">Safe and Efficient for Years</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Once your fireplace is in, taking care of it is pretty straightforward. But there are a few things you don't want to skip.
                    </p>
                    <p>
                      <strong>The 3-2-10 rule.</strong> Your chimney must be at least 3 feet above the roofline. And it needs to be 2 feet taller than anything within 10 feet of it. This includes roof peaks, dormers, whatever is nearby. This keeps draft pulling correctly and reduces fire risk.
                    </p>
                    <p>
                      <strong>Annual inspections.</strong> North Jersey winters mean heavy fireplace use from October through March. Creosote builds up faster in cold climates, and it doesn't take much to become a hazard. A yearly inspection catches cracks in the liner, creosote buildup, and any masonry damage before it turns into a bigger problem. The <CitationLink num={1} url="https://www.nfpa.org/codes-and-standards/nfpa-211-standard-development/211">NFPA recommends this</CitationLink> for every fireplace, every year.
                    </p>
                    <p>
                      <strong>Older fireplaces.</strong> If you have an existing fireplace in Wayne, Livingston, or Nutley that hasn't been used in a while, don't just light it up. Have someone look at it first. In a lot of cases, rebuilding the firebox or relining the chimney makes more sense than patching things together. You get a safer fireplace and it costs less long term than dealing with repeated repairs.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden relative">
                  <img 
                    src={fireplaceWayneScaffold} 
                    alt="Masonry fireplace installation on a house in Wayne, Passaic County, New Jersey, 07470" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section - Card Grid */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked Questions
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <Card key={index} className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="text-lg">{faq.question}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          <YouTubeShortsSection 
            title="See Our Work in"
            highlightedTitle="Action"
            videoIds={["KFgLmBVqRZQ"]}
          />

          <RelatedServices currentCategory="masonry" />

          {/* CTA Section with Form */}
          <CTASection 
            heading="Ready for a" 
            highlightedHeading="Fireplace Installation?"
            serviceName="Fireplace"
          />

          {/* Citations Accordion */}
          <section className="py-12 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                      Citations and References
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base">
                      <ul className="space-y-3">
                        {citations.map((citation) => (
                          <li key={citation.num} className="text-sm">
                            <sup>{citation.num}</sup>{" "}
                            <a 
                              href={citation.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {citation.text}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
